import { parseDistricts } from './districts';
/**
 * Analytics data aggregation utilities
 */

export interface AggregatedData {
  value: string;
  count: number;
  percentage: number;
}

/**
 * Helper to get answer value from response - handles both old and new data structures
 */
export function readDemographicPath(demographics: any, dottedKey: string): any {
  return dottedKey.split('.').reduce((acc: any, segment: string) => {
    if (acc == null) return acc;
    /*
      `district` has been stored two ways — as an object, and (for five months)
      as that object stringified. Normalize before stepping into it, or every
      district read on a stringified row silently returns undefined.
    */
    const container = typeof acc === 'string' ? parseDistricts(acc) : acc;
    return container == null ? undefined : (container as any)[segment];
  }, demographics);
}

export function getAnswerValue(response: any, questionId: string): any {
  if (questionId.startsWith('demo_')) {
    /* Dotted suffix: demo_age_range -> demographics.age_range, and
       demo_district.state_house -> demographics.district.state_house. */
    return readDemographicPath(response.answers_json?.demographics, questionId.slice(5)) ?? undefined;
  }
  if (response.answers_json?.questions?.[questionId] !== undefined) {
    return response.answers_json.questions[questionId];
  }
  if (response.answers_json?.[questionId] !== undefined) {
    return response.answers_json[questionId];
  }
  return undefined;
}

/**
 * Aggregate response data by a specific field/question
 * @param responses - Array of response objects
 * @param questionId - The question ID to aggregate by
 * @param options - Optional array of options with value/label pairs for display mapping
 */
export function aggregateResponsesByField(
  responses: any[],
  questionId: string,
  options?: { value: string; label: string }[]
): AggregatedData[] {
  if (!responses || !responses.length) {
    return [];
  }

  // Build a lookup map from option values to labels
  const labelMap = new Map<string, string>();
  if (options) {
    options.forEach(opt => {
      if (opt.value && opt.label) {
        labelMap.set(opt.value, opt.label);
      }
    });
  }

  const counts: Record<string, number> = {};
  let total = 0;

  // Count occurrences
  responses.forEach(response => {
    const value = getAnswerValue(response, questionId);
    
    if (value !== undefined && value !== null && value !== '') {
      // Skip objects that aren't arrays (like demographics)
      if (typeof value === 'object' && !Array.isArray(value)) {
        return;
      }
      
      // Handle arrays (multiselect/checkbox)
      if (Array.isArray(value)) {
        value.forEach(v => {
          const key = String(v);
          counts[key] = (counts[key] || 0) + 1;
          total++;
        });
      } else {
        const key = String(value);
        counts[key] = (counts[key] || 0) + 1;
        total++;
      }
    }
  });

  // Convert to array and calculate percentages
  // Use label from options if available, otherwise fall back to raw value
  const aggregated = Object.entries(counts)
    .map(([value, count]) => ({
      value: labelMap.get(value) || value,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0
    }))
    .sort((a, b) => b.count - a.count); // Sort by count descending

  return aggregated;
}

// Date-with-time display comes from the canonical module; the local copy
// this replaces had identical output.
export { formatDateTime as formatDate } from './dateUtils';

/**
 * Format duration in milliseconds to human-readable string
 */
export function formatDuration(durationMs?: number): string {
  if (!durationMs) return '—';
  
  const seconds = Math.round(durationMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}
